import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

export interface PendingConnection {
  service: string;
  connNum?: number;
  totalConns?: number;
  reason?: string;
}

interface UnsavedConnectionsWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingConnections: PendingConnection[];
  onConfirm: () => void;
}

export const UnsavedConnectionsWarningDialog: React.FC<
  UnsavedConnectionsWarningDialogProps
> = ({ open, onOpenChange, pendingConnections, onConfirm }) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md p-6 bg-white rounded-xl shadow-xl border border-gray-100">
        <AlertDialogHeader className="flex flex-col items-center text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <AlertDialogTitle className="text-lg font-bold text-gray-900">
            Incomplete Installation Data
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-gray-600">
            Some utility services or connections have not been saved or assigned yet. Are you sure you want to proceed to activation anyway?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {pendingConnections.length > 0 && (
          <div className="mt-4 mb-2 max-h-48 overflow-y-auto space-y-2 border border-gray-100 rounded-lg p-3 bg-gray-50/70">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Pending Connections / Services:
            </p>
            {pendingConnections.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-xs p-2 rounded bg-white border border-gray-200/60 text-gray-800"
              >
                <span className="font-semibold text-gray-900">
                  {item.service}
                  {item.connNum ? ` – Connection ${item.connNum}` : ""}
                </span>
                <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60 font-medium">
                  {item.reason || "Unsaved"}
                </span>
              </div>
            ))}
          </div>
        )}

        <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-4 sm:space-x-0">
          <AlertDialogCancel className="w-full sm:w-1/2">
            Review Connections
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="w-full sm:w-1/2 bg-amber-600 hover:bg-amber-700 text-white"
          >
            Proceed to Activation
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UnsavedConnectionsWarningDialog;
